// Create a new file: ReportTableView.tsx
import {
  Clock3,
  Download,
  Edit2,
  Eye,
  FileText,
  FolderOpen,
  Loader2,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.jsx";
import { Progress } from "../ui/progress";

const ReportTableView = ({
  reports,
  getReportProgress,
  onOpenReport,
  onGenerateReport,
  onDownloadReport,
  onEditReport,
  onDeleteReport,
  reportActionLoading,
  formatReportDate,
  templateLabel,
}) => {
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-surface-raised/50 dark:shadow-none">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-brand/20 border-b border-slate-200 dark:border-white/10">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Report Name
            </th>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Site
            </th>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Template
            </th>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Status
            </th>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Progress
            </th>
            <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-muted-foreground">
              Last Updated
            </th>
            <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-white/10">
          {reports.map((report) => {
            const isGenerating =
              reportActionLoading?.reportId === report._id &&
              reportActionLoading?.action === "generate";
            const isDownloading =
              reportActionLoading?.reportId === report._id &&
              reportActionLoading?.action === "download";
            const isEditing =
              reportActionLoading?.reportId === report._id &&
              reportActionLoading?.action === "edit";
            const isDeleting =
              reportActionLoading?.reportId === report._id &&
              reportActionLoading?.action === "delete";

            const statusColors = {
              "not-started":
                "bg-slate-100 text-slate-700 dark:bg-muted dark:text-muted-foreground",
              saved:
                "bg-blue-100 text-blue-700 dark:bg-brand/20 dark:text-link",
              "in-progress":
                "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300",
              generated:
                "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
              completed:
                "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300",
            };

            const progress = getReportProgress(report);

            return (
              <tr
                key={report._id}
                className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-md ${
                        report.status === "generated" ||
                        report.status === "completed"
                          ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
                          : "bg-blue-50 text-blue-600 dark:bg-brand/30 dark:text-link"
                      }`}
                    >
                      <FileText className="h-4 w-4" />
                    </div>
                    <span className="font-medium text-slate-900 dark:text-foreground truncate max-w-[200px]">
                      {report.reportName || "Untitled report"}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-muted-foreground">
                  {report.siteName || report.site?.siteName || "-"}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-muted text-slate-600 dark:text-muted-foreground">
                    <FolderOpen className="h-3.5 w-3.5" />
                    {templateLabel(report.templateId)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Badge
                    className={`text-xs font-medium ${
                      statusColors[report.status] || statusColors["not-started"]
                    }`}
                  >
                    {report.status || "not-started"}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-[100px]">
                    <Progress
                      value={progress}
                      className={`h-2 flex-1 ${
                        progress === 100
                          ? "bg-emerald-100 dark:bg-emerald-900/20"
                          : progress > 50
                            ? "bg-blue-100 dark:bg-brand/20"
                            : "bg-slate-100 dark:bg-muted"
                      }`}
                    />
                    <span className="text-xs font-medium text-slate-700 dark:text-muted-foreground min-w-[35px]">
                      {progress}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-500 dark:text-muted-foreground whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" />
                    {formatReportDate(report.updatedAt || report.createdAt)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpenReport(report)}
                      className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-accent"
                      title="Open Report"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-accent"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {/* <DropdownMenuItem
                          onClick={() => onSaveReport(report)}
                          disabled={report.status === "generated" || !!reportActionLoading}
                          className={
                            report.status === "generated"
                              ? "text-slate-400 opacity-60"
                              : "text-blue-700 dark:text-link"
                          }
                        >
                          {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          {isSaving ? "Saving..." : "Save Report"}
                        </DropdownMenuItem> */}

                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            onGenerateReport(report, "generate");
                          }}
                          disabled={!report._id || !!reportActionLoading}
                          className="text-indigo-700 dark:text-indigo-300"
                        >
                          {isGenerating ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <FileText className="mr-2 h-4 w-4" />
                          )}
                          {isGenerating ? "Generating..." : "Generate Report"}
                        </DropdownMenuItem>

                        {/* <DropdownMenuItem
                          onClick={() => onGenerateReport(report, "regenerate")}
                          disabled={report.status !== "generated" || !!reportActionLoading}
                          className={
                            report.status === "generated"
                              ? "text-indigo-700 dark:text-indigo-300"
                              : "text-slate-400 opacity-60"
                          }
                        >
                          {isRegenerating ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <FileText className="mr-2 h-4 w-4" />
                          )}
                          {isRegenerating ? "Re-Generating..." : "Re-Generate Report"}
                        </DropdownMenuItem> */}

                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            onDownloadReport(report);
                          }}
                          disabled={
                            report.status !== "generated" ||
                            !!reportActionLoading
                          }
                          className={
                            report.status === "generated"
                              ? "text-emerald-700 dark:text-emerald-300"
                              : "text-slate-400 opacity-60"
                          }
                        >
                          {isDownloading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="mr-2 h-4 w-4" />
                          )}
                          {isDownloading ? "Opening..." : "Download Report"}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => onEditReport(report)}
                          disabled={isEditing || !!reportActionLoading}
                          className={
                            isEditing || !!reportActionLoading
                              ? "text-slate-400 opacity-60"
                              : "text-slate-700 dark:text-foreground"
                          }
                        >
                          {isEditing ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Edit2 className="mr-2 h-4 w-4" />
                          )}
                          {isEditing ? "Updating..." : "Edit"}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onSelect={() => onDeleteReport(report)}
                          className="text-red-600 dark:text-red-400"
                          disabled={isDeleting || !!reportActionLoading}
                        >
                          {isDeleting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="mr-2 h-4 w-4" />
                          )}
                          {isDeleting ? "Deleting" : "Delete"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReportTableView;
